import React, { useState, useRef, useEffect, useMemo } from 'react'
import { X, Download, ExternalLink, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react'
import { Document, Page, pdfjs } from 'react-pdf'
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { logger } from '../utils/logger'

// Конфигурация PDF worker (используем собранный URL из Vite, с CDN fallback)
const configurePDFWorker = () => {
  try {
    if (workerUrl) {
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl as unknown as string
      logger.info('✅ PDF Worker настроен (bundled):', workerUrl)
      return
    }
    // Если по какой-то причине workerUrl пустой — fallback
    pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`
    logger.warn('⚠️ workerUrl пустой, использован CDN fallback')
  } catch (error) {
    logger.error('❌ Ошибка конфигурации PDF worker:', error)
    pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`
  }
}

// Инициализируем worker при загрузке модуля
configurePDFWorker()

interface PDFViewerProps {
  isOpen: boolean
  onClose: () => void
  fileUrl: string
  fileName: string
  onDownload?: () => void
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  isOpen,
  onClose,
  fileUrl,
  fileName,
  onDownload
}) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [scale, setScale] = useState<number>(1.0)
  const [containerWidth, setContainerWidth] = useState<number>(0)
  // Плавное масштабирование через CSS transforms
  const [isZooming, setIsZooming] = useState<boolean>(false)
  const [tempScale, setTempScale] = useState<number>(1.0)
  // Touch gestures
  const [pinchStartDistance, setPinchStartDistance] = useState<number | null>(null)
  const [pinchStartScale, setPinchStartScale] = useState<number>(1.0)
  const [swipeStartX, setSwipeStartX] = useState<number | null>(null)
  const [swipeStartY, setSwipeStartY] = useState<number | null>(null)
  const isPanningRef = useRef<boolean>(false)
  const startScrollLeftRef = useRef<number>(0)
  const startScrollTopRef = useRef<number>(0)
  const pageContainerRef = useRef<HTMLDivElement>(null)
  // Double-tap для сброса масштаба
  const lastTapRef = useRef<number>(0)
  // Минимум состояний: полагаемся на загрузку внутри <Document />
  const containerRef = useRef<HTMLDivElement>(null)

  // Сброс параметров при открытии/смене файла
  useEffect(() => {
    if (!isOpen || !fileUrl) return
    setLoading(true)
    setError(null)
    setPageNumber(1)
    setScale(1.0)
  }, [isOpen, fileUrl])

  // Блокировка прокрутки body при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      // Сохраняем текущие стили и позицию скролла
      const originalOverflow = document.body.style.overflow
      const originalPosition = document.body.style.position
      const originalWidth = document.body.style.width
      const originalHeight = document.body.style.height
      const originalTop = document.body.style.top
      const scrollY = window.scrollY
      
      // Блокируем прокрутку с сохранением позиции
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.height = '100%'
      
      // Восстанавливаем при закрытии или размонтировании
      return () => {
        document.body.style.overflow = originalOverflow
        document.body.style.position = originalPosition
        document.body.style.width = originalWidth
        document.body.style.height = originalHeight
        document.body.style.top = originalTop
        
        // Восстанавливаем позицию скролла
        if (scrollY > 0) {
          window.scrollTo(0, scrollY)
        }
      }
    }
  }, [isOpen])

  // Дополнительная очистка при размонтировании компонента
  useEffect(() => {
    return () => {
      // Принудительно восстанавливаем стили body при размонтировании
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
      document.body.style.height = ''
      document.body.style.top = ''
      
      // Убеждаемся, что скролл работает
      setTimeout(() => {
        document.body.style.overflow = ''
        document.body.style.position = ''
      }, 0)
    }
  }, [])

  // Обработчик клавиши Escape для закрытия модального окна
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  // Fit-to-width: отслеживаем ширину контейнера
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = () => setContainerWidth(el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    window.addEventListener('resize', update)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [])

  // Стабильные опции для <Document />, чтобы не пересоздавать PDF транспорт на каждом ререндере
  const documentOptions = useMemo(() => ({
    verbosity: 0,
    isEvalSupported: false,
    disableStream: true,
    disableRange: true,
    disableAutoFetch: true,
    useWorkerFetch: false,
    enableXfa: false,
    stopAtErrors: false,
    maxImageSize: 16777216,
    cMapPacked: true,
  }), [])

  // Helpers for touch gestures
  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))
  const getDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.hypot(dx, dy)
  }

  // Object URL не используется — cleanup не требуется

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Мемоизированный файловый объект для react-pdf (используем прямой URL)
  const pdfFile = useMemo(() => {
    if (!fileUrl) return null
    const fullUrl = fileUrl.startsWith('http')
      ? fileUrl
      : `${window.location.origin}${fileUrl}`
    return { url: fullUrl }
  }, [fileUrl])

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setLoading(false)
    setError(null)
  }

  const onDocumentLoadError = (error: Error) => {
    logger.error('❌ Ошибка загрузки PDF документа:', error)
    logger.error('❌ Детали ошибки:', error.message, error.stack)
    logger.error('❌ Worker URL:', pdfjs.GlobalWorkerOptions.workerSrc)
    
    // Если ошибка связана с worker, пытаемся переключиться на CDN fallback
    if (error.message.includes('worker') || error.message.includes('Worker')) {
      logger.warn('🔄 Переключаемся на CDN worker из-за ошибки worker')
      pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`
      // Даем пользователю возможность попробовать снова
      setError(`Проблема с загрузкой PDF worker. Попробуйте обновить страницу или открыть файл заново.`)
    } else {
      setError(`Не удалось загрузить PDF файл: ${error.message}`)
    }
    setLoading(false)
  }

  const onDocumentLoadProgress = ({ loaded, total }: { loaded: number; total: number }) => {
    // Прогресс загрузки (без логирования для клиентов)
  }

  const onSourceError = (error: Error) => {
    logger.error('❌ Ошибка источника PDF:', error)
    logger.error('❌ Возможно проблема с worker или CORS')
    logger.error('❌ Текущий worker URL:', pdfjs.GlobalWorkerOptions.workerSrc)
    
    // Если ошибка связана с worker, пытаемся использовать CDN fallback
    if (error.message.includes('worker') || error.message.includes('Worker') || 
        error.message.includes('fetch') || error.message.includes('dynamically imported')) {
      logger.warn('🔄 Ошибка источника связана с worker, переключаемся на CDN')
      pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`
      setError(`Проблема с загрузкой PDF worker. Попробуйте обновить страницу.`)
    } else {
      setError(`Ошибка источника PDF: ${error.message}`)
    }
    setLoading(false)
  }



  const onSourceSuccess = () => {

  }

  const handlePrevPage = () => {
    setPageNumber(prev => Math.max(1, prev - 1))
  }

  const handleNextPage = () => {
    setPageNumber(prev => Math.min(numPages, prev + 1))
  }

  const handleZoomIn = () => {
    setScale(prev => Math.min(5.0, Number((prev + 0.2).toFixed(2))))
  }

  const handleZoomOut = () => {
    setScale(prev => Math.max(0.5, Number((prev - 0.2).toFixed(2))))
  }

  const handleResetZoom = () => {
    setScale(1.0)
    setTempScale(1.0)
    setIsZooming(false)
  }

  // Убираем сложную логику с ResizeObserver - используем простой scale





  if (!isOpen) return null

  return (
    <div
      className="pdf-viewer-modal fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      style={{
        paddingTop: 'max(env(safe-area-inset-top), 0px)',
        paddingBottom: 'max(env(safe-area-inset-bottom), 0px)',
        paddingLeft: 'max(env(safe-area-inset-left), 0px)',
        paddingRight: 'max(env(safe-area-inset-right), 0px)',
      }}
      onClick={handleBackdropClick}
    >
      {/* Модальное окно */}
      <div className="relative w-full h-full max-w-7xl max-h-full flex flex-col m-2 md:m-4">
        {/* Заголовок с кнопками управления */}
        <div className="flex items-center justify-between mb-2 px-3 md:px-4 py-2 bg-white/95 backdrop-blur-sm rounded-t-lg shadow-sm">
          <h2 className="text-sm md:text-lg font-semibold text-gray-900 truncate flex-1 mr-2">
            {fileName}
          </h2>
          
          <div className="flex items-center space-x-1 md:space-x-2">
            {/* Навигация по страницам */}
            {numPages > 1 && (
              <>
                <button
                  onClick={handlePrevPage}
                  disabled={pageNumber <= 1}
                  className="p-1.5 md:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Предыдущая страница"
                >
                  <ChevronLeft size={16} className="md:w-5 md:h-5" />
                </button>
                
                <span className="text-xs md:text-sm text-gray-600 px-2">
                  {pageNumber} / {numPages}
                </span>
                
                <button
                  onClick={handleNextPage}
                  disabled={pageNumber >= numPages}
                  className="p-1.5 md:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Следующая страница"
                >
                  <ChevronRight size={16} className="md:w-5 md:h-5" />
                </button>
                
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
              </>
            )}
            
            {/* Масштабирование */}
            <button
              onClick={handleZoomOut}
              className="p-1.5 md:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
              title="Уменьшить"
            >
              <ZoomOut size={16} className="md:w-5 md:h-5" />
            </button>
            
            <button
              onClick={handleResetZoom}
              className="text-xs md:text-sm text-gray-600 hover:text-gray-900 px-2 py-1 hover:bg-gray-100 rounded transition-colors"
              title="Сбросить масштаб"
            >
              {Math.round(scale * 100)}%
            </button>
            
            <button
              onClick={handleZoomIn}
              className="p-1.5 md:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
              title="Увеличить"
            >
              <ZoomIn size={16} className="md:w-5 md:h-5" />
            </button>
            
            <div className="w-px h-4 bg-gray-300 mx-1"></div>
            
            {onDownload && (
              <button
                onClick={onDownload}
                className="p-1.5 md:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                title="Скачать файл"
              >
                <Download size={16} className="md:w-5 md:h-5" />
              </button>
            )}
            
            <button
              onClick={() => window.open(fileUrl, '_blank')}
              className="p-1.5 md:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
              title="Открыть в новой вкладке"
            >
              <ExternalLink size={16} className="md:w-5 md:h-5" />
            </button>
            
            <button
              onClick={onClose}
              className="p-1.5 md:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
              title="Закрыть"
            >
              <X size={16} className="md:w-5 md:h-5" />
            </button>
          </div>
        </div>

        {/* Контент PDF */}
        <div 
          ref={containerRef}
          className="flex-1 bg-gray-100 rounded-b-lg overflow-auto touch-pan-y"
          style={{
            minHeight: 0,
            // разрешаем естественный скролл по обеим осям внутри контейнера,
            // чтобы жесты панорамирования не вызывали смену страницы
            touchAction: 'manipulation',
          }}
          onTouchStart={(e) => {
            if (e.touches.length === 2) {
              // Pinch-to-zoom
              const dist = getDistance(e.touches)
              setPinchStartDistance(dist)
              setPinchStartScale(scale)
              setTempScale(scale)
              setIsZooming(true)
            } else if (e.touches.length === 1) {
              // Single touch - потенциальный swipe или pan
              setSwipeStartX(e.touches[0].clientX)
              setSwipeStartY(e.touches[0].clientY)
              isPanningRef.current = false
              const el = containerRef.current
              if (el) {
                startScrollLeftRef.current = el.scrollLeft
                startScrollTopRef.current = el.scrollTop
              }
            }
          }}
          onTouchMove={(e) => {
            if (e.touches.length === 2 && pinchStartDistance) {
              // Плавное масштабирование через CSS transform
              e.preventDefault()
              const newDist = getDistance(e.touches)
              const ratio = newDist / pinchStartDistance
              const newTempScale = clamp(Number((pinchStartScale * ratio).toFixed(2)), 0.5, 5.0)
              setTempScale(newTempScale)
            } else if (e.touches.length === 1 && swipeStartX !== null && swipeStartY !== null) {
              // Проверяем, панорамирует ли пользователь
              const el = containerRef.current
              if (el) {
                const dxScroll = Math.abs(el.scrollLeft - startScrollLeftRef.current)
                const dyScroll = Math.abs(el.scrollTop - startScrollTopRef.current)
                const dxTouch = Math.abs(e.touches[0].clientX - swipeStartX)
                const dyTouch = Math.abs(e.touches[0].clientY - swipeStartY)
                // Если есть скролл или значительное движение по Y - это панорамирование
                if (dxScroll > 5 || dyScroll > 5 || dyTouch > 30) {
                  isPanningRef.current = true
                }
              }
            }
          }}
          onTouchEnd={(e) => {
            if (pinchStartDistance) {
              // Завершение pinch-to-zoom
              setPinchStartDistance(null)
              setScale(Number(tempScale.toFixed(2)))
              setIsZooming(false)
            }
            if (swipeStartX !== null && swipeStartY !== null) {
              const endX = e.changedTouches[0]?.clientX ?? swipeStartX
              const endY = e.changedTouches[0]?.clientY ?? swipeStartY
              const dx = endX - swipeStartX
              const dy = endY - swipeStartY
              const threshold = Math.min(120, Math.max(50, (containerWidth || 300) * 0.15))
              
              // Проверяем double-tap для сброса масштаба (только на мобильных)
              const now = Date.now()
              const timeDiff = now - lastTapRef.current
              const isMobile = 'ontouchstart' in window
              if (isMobile && timeDiff < 300 && Math.abs(dx) < 20 && Math.abs(dy) < 20) {
                // Double-tap на мобильных - сбрасываем масштаб
                handleResetZoom()
                lastTapRef.current = 0
              } else {
                lastTapRef.current = now
                
                // Смена страницы только при горизонтальном свайпе без панорамирования и при масштабе ~1
                if (!isPanningRef.current && scale <= 1.1 && Math.abs(dx) > threshold && Math.abs(dy) < 80) {
                  if (dx > 0 && pageNumber > 1) {
                    setPageNumber(prev => Math.max(1, prev - 1))
                  } else if (dx < 0 && pageNumber < numPages) {
                    setPageNumber(prev => Math.min(numPages, prev + 1))
                  }
                }
              }
              setSwipeStartX(null)
              setSwipeStartY(null)
            }
          }}
        >
          <div className="flex items-center justify-center min-h-full p-4">
            {/* Внешний лоадер убран, используем только loading у <Document /> */}
            
            {error && (
              <div className="text-center">
                <div className="text-red-500 text-6xl mb-4">⚠️</div>
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={() => window.open(fileUrl, '_blank')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Открыть в новой вкладке
                </button>
              </div>
            )}
            
            {/* Как только есть любые данные файла и нет error — монтируем Document */}
            {!error && pdfFile && (
              <div className="w-full h-full flex items-center justify-center">

                <Document
                  file={pdfFile}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  onLoadProgress={onDocumentLoadProgress}
                  onSourceError={onSourceError}
                  onSourceSuccess={onSourceSuccess}
                  loading={
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Загрузка PDF...</p>
                    </div>
                  }
                  error={
                    <div className="text-center">
                      <div className="text-red-500 text-6xl mb-4">⚠️</div>
                      <p className="text-red-600 mb-4">Не удалось отобразить PDF файл</p>
                      <button
                        onClick={() => window.open(fileUrl, '_blank')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Открыть в новой вкладке
                      </button>
                    </div>
                  }
                  options={documentOptions}
                >
                  {/* Контейнер для плавного CSS-масштабирования */}
                  <div
                    ref={pageContainerRef}
                    style={{
                      transform: isZooming ? `scale(${tempScale / scale})` : 'scale(1)',
                      transformOrigin: 'center center',
                      transition: isZooming ? 'none' : 'transform 0.2s ease-out',
                    }}
                    onDoubleClick={(e) => {
                      // Double-click на десктопе: увеличение/сброс
                      e.preventDefault()
                      if (scale <= 1.1) {
                        // Увеличиваем до 200%
                        setScale(2.0)
                        setTempScale(2.0)
                      } else {
                        // Сбрасываем к 100%
                        handleResetZoom()
                      }
                    }}
                  >
                    <Page
                      pageNumber={pageNumber}
                      width={containerWidth || undefined}
                      scale={scale}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      className="shadow-lg"
                      devicePixelRatio={window.devicePixelRatio || 1}
                    />
                  </div>
                </Document>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PDFViewer
